import React, { useState } from 'react';
import { IconButton, Stack, Text, Link } from '@fluentui/react';

interface IExpandableTextBoxProps {
    text: string;
    title?: string;
    theme: any;
    maxLength?: number;
    isValidHyperlink?: (input: string) => boolean;
    onCopy?: () => void;
    showCopyButton?: boolean;
}

export const ExpandableTextBox: React.FC<IExpandableTextBoxProps> = ({
    text,
    title = 'Details',
    theme,
    maxLength = 100,
    isValidHyperlink,
    onCopy,
    showCopyButton = true,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const shouldTruncate = text.length > maxLength;
    const displayText =
        shouldTruncate && !isExpanded
            ? text.substring(0, maxLength) + '...'
            : text;

    return (
        <Stack
            style={{
                backgroundColor: theme.palette.neutralLighterAlt,
                border: `1px solid ${theme.palette.neutralLight}`,
                borderRadius: 4,
                padding: '8px',
                marginTop: '4px',
                position: 'relative',
            }}
            tokens={{ childrenGap: 5 }}
        >
            {/* Header with expand/collapse and copy buttons */}
            <Stack
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
            >
                <Text
                    variant="medium"
                    style={{
                        color: theme.palette.neutralSecondary,
                        fontWeight: 600,
                    }}
                >
                    {title}
                </Text>
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                    {shouldTruncate && (
                        <IconButton
                            iconProps={{
                                iconName: isExpanded
                                    ? 'ChevronUp'
                                    : 'ChevronDown',
                            }}
                            onClick={() => setIsExpanded(!isExpanded)}
                            title={isExpanded ? 'Collapse' : 'Expand'}
                            ariaLabel={isExpanded ? 'Collapse' : 'Expand'}
                            aria-expanded={isExpanded}
                            styles={{
                                root: { minWidth: 16, width: 16, height: 16 },
                                icon: { fontSize: 16 },
                            }}
                        />
                    )}
                    {showCopyButton && onCopy && (
                        <IconButton
                            iconProps={{ iconName: 'Copy' }}
                            onClick={onCopy}
                            title="Copy to clipboard"
                            ariaLabel="Copy to clipboard"
                            styles={{
                                root: { minWidth: 16, width: 16, height: 16 },
                                icon: { fontSize: 16 },
                            }}
                        />
                    )}
                </Stack>
            </Stack>

            {/* Content */}
            <div
                style={{
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: '11px',
                    lineHeight: '1.4',
                    wordBreak: 'break-all',
                    whiteSpace: 'pre-wrap',
                    maxHeight: isExpanded ? '200px' : 'auto',
                    overflow: isExpanded ? 'auto' : 'visible',
                }}
            >
                {isValidHyperlink && isValidHyperlink(text) ? (
                    <Link
                        href={text}
                        underline
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '11px' }}
                    >
                        {displayText}
                    </Link>
                ) : (
                    <Text style={{ color: theme.palette.neutralPrimary }}>
                        {displayText}
                    </Text>
                )}
            </div>

            {shouldTruncate && !isExpanded && (
                <Text
                    variant="small"
                    style={{
                        color: theme.palette.themePrimary,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                    }}
                    onClick={() => setIsExpanded(true)}
                >
                    Show more
                </Text>
            )}
        </Stack>
    );
};
